/**
 * @fileoverview Onboarding Schemas & Types
 * @module types/onboarding
 *
 * Single source of truth for onboarding data structures
 * Extends and reuses existing schemas from test-session.contract.ts
 *
 * Used by:
 * - Frontend: Form validation & type safety
 * - Backend: API validation & database storage
 */

import { z } from 'zod';

// ============================================================================
// BASE SCHEMAS (Reused from test-session.contract.ts)
// ============================================================================

/**
 * Gender enum for pronouns support
 * Used to generate grammatically correct personalized messages in German
 */
export const ChildGenderEnum = z.enum(['male', 'female', 'other']);
export type ChildGender = z.infer<typeof ChildGenderEnum>;

/**
 * Basic Child Info Schema
 * Matches the schema used in CreateTestSessionSchema.basicInfo
 * This is the backend contract - frontend extends this
 * ✅ EXTENDED: Now includes childInterests and childGender
 */
export const ChildBasicInfoSchema = z.object({
  childName: z.string().min(1, 'Child name is required').trim(),
  childAge: z.number().int().positive('Child age must be positive'),
  childSchool: z.string().default(''),
  childSituation: z.string().default(''),
  childInterests: z.string().optional(),
  childGender: ChildGenderEnum.default('other'),
});
export type ChildBasicInfo = z.infer<typeof ChildBasicInfoSchema>;

/**
 * Onboarding Context Schema
 * ✅ NEW: Stores onboarding-specific data (challenges, goals, parent feeling)
 * Separate from basic info to maintain clean separation of concerns
 */
export const OnboardingContextSchema = z.object({
  challenges: z.array(z.string()).optional(),
  parentFeelings: z.array(z.string()).optional(),
  selectedGoals: z.array(z.string()).optional(),
  startedAt: z.iso.datetime().optional(),
  completedAt: z.iso.datetime().optional(),
});
export type OnboardingContext = z.infer<typeof OnboardingContextSchema>;

// ============================================================================
// ONBOARDING-SPECIFIC SCHEMAS
// ============================================================================

/**
 * Complete Onboarding Data Schema
 *
 * IMPORTANT: This schema represents ONLY the onboarding-specific fields stored in the
 * TestResult.onboarding JSONB column. Basic info (childName, childAge, childSchool,
 * childSituation) are stored in separate TestResult fields.
 *
 * Frontend validation rules (German messages for UX)
 */
export const OnboardingDataSchema = z.object({
  // Basic info fields - OPTIONAL here because they're stored separately in TestResult
  // These are included for frontend form validation but NOT stored in onboarding column
  childName: z.string()
    .min(1, 'Name ist erforderlich')
    .trim()
    .optional(),
  childAge: z.number()
    .int()
    .min(3, 'Alter muss mindestens 3 sein')
    .max(18, 'Alter darf maximal 18 sein')
    .optional(),
  childSchool: z.string()
    .min(1, 'Schultyp ist erforderlich')
    .trim()
    .optional(),
  childSituation: z.string()
    .min(1, 'Lernsituation ist erforderlich')
    .trim()
    .optional(),
  childInterests: z.string().optional(),
  childGender: ChildGenderEnum.optional(),

  // Onboarding-specific fields (Step 2-4) - These ARE stored in onboarding column

  /**
   * Step 2: Selected challenges
   */
  challenges: z.array(z.string())
    .optional(),

  /**
   * Step 3: Parent's current feelings (multiple choice)
   */
  parentFeelings: z.array(z.string())
    .optional(),

  /**
   * Step 4: Selected goals (max 3)
   */
  selectedGoals: z.array(z.string())
    .max(3, 'Maximal 3 Ziele können ausgewählt werden')
    .optional(),

  // Metadata (ISO datetime strings for API compatibility)
  completedAt: z.iso.datetime().optional(),
  startedAt: z.iso.datetime().optional(),
});

/**
 * TypeScript type inferred from Zod schema
 * Use this type in your frontend code
 */
export type OnboardingData = z.infer<typeof OnboardingDataSchema>;

// ============================================================================
// PARTIAL SCHEMAS (For Step-by-Step Validation)
// ============================================================================

/**
 * Step 1: Child Introduction
 */
export const OnboardingStep1Schema = OnboardingDataSchema.pick({
  childName: true,
  childAge: true,
  childSchool: true,
  childSituation: true,
  childInterests: true,
});
export type OnboardingStep1 = z.infer<typeof OnboardingStep1Schema>;

/**
 * Step 2: Challenges
 */
export const OnboardingStep2Schema = OnboardingDataSchema.pick({
  challenges: true,
});
export type OnboardingStep2 = z.infer<typeof OnboardingStep2Schema>;

/**
 * Step 3: Parent Feelings
 */
export const OnboardingStep3Schema = OnboardingDataSchema.pick({
  parentFeelings: true,
});
export type OnboardingStep3 = z.infer<typeof OnboardingStep3Schema>;

/**
 * Step 4: Goals
 */
export const OnboardingStep4Schema = OnboardingDataSchema.pick({
  selectedGoals: true,
});
export type OnboardingStep4 = z.infer<typeof OnboardingStep4Schema>;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convert OnboardingData to ChildBasicInfo (for API submission)
 * Extracts only the fields needed for test session creation
 *
 * NOTE: This function is used during frontend form submission where all basic info
 * fields are required. The OnboardingData schema allows them to be optional for
 * API responses, but during form submission they must be present.
 * ✅ UPDATED: Now includes childInterests and childGender
 */
export function toChildBasicInfo(data: OnboardingData): ChildBasicInfo {
  return {
    childName: data.childName || '',
    childAge: typeof data.childAge === 'number' ? data.childAge : 0,
    childSchool: data.childSchool || '',
    childSituation: data.childSituation || '',
    childInterests: data.childInterests,
    childGender: data.childGender || 'other',
  };
}

/**
 * Convert OnboardingData to OnboardingContext (for API submission)
 * Extracts only the onboarding-specific fields
 * ✅ NEW: Helper function for extracting onboarding context
 */
export function toOnboardingContext(data: OnboardingData): OnboardingContext {
  return {
    challenges: data.challenges,
    parentFeelings: data.parentFeelings,
    selectedGoals: data.selectedGoals,
    startedAt: data.startedAt,
    completedAt: data.completedAt,
  };
}

/**
 * Validate a single field using Zod
 * Returns error message or null if valid
 */
export function validateField<K extends keyof OnboardingData>(
  field: K,
  value: OnboardingData[K]
): string | null {
  const fieldSchema = OnboardingDataSchema.pick({ [field]: true });
  const result = fieldSchema.safeParse({ [field]: value });

  if (!result.success) {
    return result.error.issues[0]?.message || 'Invalid value';
  }

  return null;
}

/**
 * Validate complete onboarding data
 * Returns { success: true, data } or { success: false, errors }
 */
export function validateOnboardingData(data: unknown):
  | { success: true; data: OnboardingData }
  | { success: false; errors: Array<{ field: string; message: string }> }
{
  const result = OnboardingDataSchema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  return {
    success: false,
    errors: result.error.issues.map(issue => ({
      field: String(issue.path[0] || ''),
      message: issue.message,
    })),
  };
}

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type OnboardingValidationResult = ReturnType<typeof OnboardingDataSchema.safeParse>;
export type OnboardingValidationError = { field: string; message: string };
